import { html, LitElement } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { t, Translator } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { getCookie, setCookie } from '@cloudsuite/mosaic-shared/scripts/cookie.js'

const DISPLAY_MODES = ['grid', 'list'] as const
type DisplayMode = typeof DISPLAY_MODES[number]

@customElement('csc-display-modes')
export class CSCDisplayModes extends LitElement {

  /** @deprecated The labels are the `csc-display-modes.grid` and `csc-display-modes.list` translations. */
  @property({ type: Object, attribute: 'display-modes' })
  set displayModes(_value: Record<string, string>) {
    console.warn('<csc-display-modes> "display-modes" is deprecated, the labels are the "csc-display-modes.grid" and "csc-display-modes.list" translations.')
  }

  get displayModes(): Record<DisplayMode, string> {
    return { grid: this.getLabel('grid'), list: this.getLabel('list') }
  }

  /** When set, component is controlled - uses this value and does not persist to cookie */
  @property({ type: String, attribute: 'value' })
  value?: DisplayMode

  @state()
  private mode: DisplayMode = 'grid'

  private cookieKey = 'cs_display_mode'

  override firstUpdated() {
    if (this.value !== undefined) return
    const cookieValue = getCookie(this.cookieKey)

    if (this.isDisplayMode(cookieValue)) {
      this.mode = cookieValue
    } else {
      this.mode = DISPLAY_MODES[0]
      setCookie(this.cookieKey, this.mode)
    }
  }

  private get displayedMode(): DisplayMode {
    return this.value ?? this.mode
  }

  private isDisplayMode(value: string | null | undefined): value is DisplayMode {
    return DISPLAY_MODES.includes(value as DisplayMode)
  }

  private getOptionLabel(mode: DisplayMode) {
    return mode === 'grid'
      ? t('grid', { ns: 'csc-display-modes', default: 'Grid' })
      : t('list', { ns: 'csc-display-modes', default: 'List' })
  }

  private getLabel(mode: DisplayMode): string {
    return mode === 'grid'
      ? new Translator('grid', { ns: 'csc-display-modes', default: 'Grid' }).translate()
      : new Translator('list', { ns: 'csc-display-modes', default: 'List' }).translate()
  }

  private handleModeChange(mode: DisplayMode) {
    if (this.displayedMode === mode) return
    if (this.value === undefined) {
      this.mode = mode
      setCookie(this.cookieKey, mode)
    }
    this.dispatchEvent(new CustomEvent('display-mode-changed', {
      detail: { mode },
      bubbles: true,
      composed: true,
    }))
  }

  override render() {
    return html`
      <cse-form-field class="csc-display-modes">
        <cse-group>
          <cse-label for="display_modes_dropdown" variant="body-s" class="label">
            ${t('label', { ns: 'csc-display-modes', default: 'View:' })}
          </cse-label>

          <cse-dropdown
            id="display_modes_dropdown"
            value="${this.getLabel(this.displayedMode)}"
            alignment="right"
            class="display-mode-dropdown"
          >
            ${DISPLAY_MODES.map(mode => html`
              <cse-option
                value="${mode}"
                @click="${() => this.handleModeChange(mode)}"
                ?selected=${this.displayedMode === mode}
              >
                ${this.getOptionLabel(mode)}
              </cse-option>
            `)}
          </cse-dropdown>
        </cse-group>
      </cse-form-field>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-display-modes': CSCDisplayModes
  }
}
